import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Package, Plus, AlertTriangle, TrendingDown, Clock, ShoppingCart,
  Loader2, Sparkles, X, Boxes, Trash2, Pencil, ArrowDownCircle, ArrowUpCircle,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import PaginationBar from "@/components/PaginationBar";

const REASON_LABEL = {
  purchase: "Compra", sale: "Venda", internal_use: "Uso interno",
  procedure: "Atendimento", loss: "Perda", breakage: "Quebra",
  expiry: "Vencimento", discard: "Descarte", adjustment: "Ajuste",
  inventory_count: "Contagem", transfer_in: "Transf. entrada",
  transfer_out: "Transf. saída", return_supplier: "Devolução",
};
const MODE_LABEL = { sale: "Venda", internal: "Uso interno", both: "Ambos" };
const REC_TYPE = {
  buy: { icon: ShoppingCart, color: "text-blue-600 bg-blue-500/10 border-blue-500/30" },
  stop_buying: { icon: TrendingDown, color: "text-zinc-700 bg-zinc-500/10 border-zinc-500/30" },
  promo: { icon: Sparkles, color: "text-amber-700 bg-amber-500/10 border-amber-500/30" },
  discard: { icon: Trash2, color: "text-red-700 bg-red-500/10 border-red-500/30" },
  review_dormant: { icon: Clock, color: "text-purple-700 bg-purple-500/10 border-purple-500/30" },
  return_supplier: { icon: ArrowUpCircle, color: "text-zinc-700 bg-zinc-500/10 border-zinc-500/30" },
};
const PRIORITY_STYLE = {
  high: "bg-red-500/10 text-red-700 border-red-500/30",
  medium: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  low: "bg-zinc-500/10 text-zinc-700 border-zinc-500/30",
};
const PRIORITY_LABEL = {
  high: "Alta",
  medium: "Média",
  low: "Baixa",
};

function fmtBRL(v) {
  return (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return iso; }
}
function daysUntil(iso) {
  if (!iso) return null;
  const now = new Date(); const d = new Date(iso);
  return Math.round((d - now) / (1000 * 60 * 60 * 24));
}

export default function InventoryTab() {
  const [params] = useSearchParams();
  const sub = params.get("sub") || "dashboard";
  // Aceita ambos nomes ("painel"/"dashboard", "products"/"produtos", "suppliers"/"fornecedores")
  const norm = ({
    painel: "dashboard",
    dashboard: "dashboard",
    products: "products",
    produtos: "products",
    suppliers: "suppliers",
    fornecedores: "suppliers",
  })[sub] || "dashboard";

  return (
    <div data-testid="inventory-root">
      {norm === "dashboard" && <InventoryDashboard />}
      {norm === "products" && <ProductsList />}
      {norm === "suppliers" && <SuppliersList />}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Dashboard
// -----------------------------------------------------------------------------
function InventoryDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/tenant/inventory/dashboard"); setData(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const dismiss = async (r) => {
    try {
      await api.post("/tenant/inventory/recommendations/dismiss", { product_id: r.product_id, type: r.type });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading || !data) return <div className="p-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></div>;
  const { kpis, recommendations, recommendations_total } = data;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Kpi icon={Boxes} label="Produtos ativos" value={kpis.total_products} />
        <Kpi icon={Package} label="Valor em estoque" value={fmtBRL(kpis.total_value)} />
        <Kpi icon={AlertTriangle} label="Estoque baixo" value={kpis.low_stock_count} tone={kpis.low_stock_count > 0 ? "warning" : "ok"} />
        <Kpi icon={Clock} label="Próx. vencimento" value={kpis.near_expiry_batches} tone={kpis.near_expiry_batches > 0 ? "warning" : "ok"} />
        <Kpi icon={Trash2} label="Vencidos" value={kpis.expired_batches} tone={kpis.expired_batches > 0 ? "danger" : "ok"} />
      </div>

      <Card className="p-5 rounded-tenant border-border" data-testid="inv-recommendations">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Ações sugeridas</div>
            <div className="font-display font-semibold text-lg">
              {recommendations_total > 0
                ? `${recommendations_total} coisa${recommendations_total > 1 ? "s" : ""} pra você olhar`
                : "Tudo tranquilo por aqui"}
            </div>
          </div>
        </div>
        {recommendations.length === 0 ? (
          <div className="text-sm text-muted-foreground italic py-6 text-center">
            Sem recomendações ativas. Estoque saudável.
          </div>
        ) : (
          <div className="space-y-2">
            {recommendations.map((r, idx) => {
              const meta = REC_TYPE[r.type] || REC_TYPE.buy;
              const Icon = meta.icon;
              return (
                <div
                  key={`${r.product_id}-${r.type}-${idx}`}
                  className={`p-3 rounded-lg border ${meta.color} flex items-start justify-between gap-3`}
                  data-testid={`rec-${r.type}-${r.product_id}`}
                >
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <Icon className="w-4 h-4 mt-0.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm">{r.title}</span>
                        <Badge variant="outline" className={PRIORITY_STYLE[r.priority]}>{PRIORITY_LABEL[r.priority] || r.priority}</Badge>
                      </div>
                      <p className="text-xs mt-1 opacity-90">{r.reason}</p>
                      {r.action_hint && <p className="text-xs mt-1 italic">{r.action_hint}</p>}
                    </div>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => dismiss(r)} title="Dispensar">
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tone = "ok" }) {
  const toneStyle = tone === "warning" ? "text-amber-700" : tone === "danger" ? "text-red-700" : "";
  return (
    <Card className="p-4 rounded-tenant border-border">
      <div className="flex items-start justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <Icon className="w-4 h-4 text-tenant" strokeWidth={1.5} />
      </div>
      <div className={`font-display font-semibold text-2xl ${toneStyle}`}>{value}</div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Products
// -----------------------------------------------------------------------------
function ProductsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [batchTarget, setBatchTarget] = useState(null);
  const [saleTarget, setSaleTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null); // product being edited
  const [deleteTarget, setDeleteTarget] = useState(null); // product pending confirmation
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      const { data } = await api.get(`/tenant/inventory/products?${params}`);
      setItems(data.items || []);
      setPage(1); // reset paginação a cada busca
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = items.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <Input placeholder="Buscar produto..." value={q} onChange={(e) => setQ(e.target.value)}
          className="max-w-xs" data-testid="inv-search" />
        <div className="flex-1" />
        <Button variant="outline" onClick={() => setSaleTarget({})} data-testid="inv-new-sale">
          <ShoppingCart className="w-3.5 h-3.5 mr-1" /> Venda avulsa
        </Button>
        <Button onClick={() => setOpenNew(true)} data-testid="inv-new-product"
          style={{ background: "hsl(var(--tenant-primary))", color: "hsl(var(--tenant-primary-foreground))" }}>
          <Plus className="w-4 h-4 mr-1" /> Novo produto
        </Button>
      </div>

      {loading ? (
        <div className="p-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center rounded-tenant border-border">
          <Package className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
          <div className="font-semibold">Nenhum produto ainda</div>
          <div className="text-sm text-muted-foreground mt-1">Cadastre o primeiro pra começar a controlar o estoque.</div>
        </Card>
      ) : (
        <Card className="rounded-tenant border-border overflow-hidden">
          <div className="overflow-x-auto -mx-px">
          <table className="w-full text-sm min-w-[560px]">
            <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-3 whitespace-nowrap">Produto</th>
                <th className="text-left px-4 py-3 whitespace-nowrap">Categoria</th>
                <th className="text-left px-4 py-3 whitespace-nowrap">Modo</th>
                <th className="text-right px-4 py-3 whitespace-nowrap">Em estoque</th>
                <th className="text-right px-4 py-3 whitespace-nowrap">Mínimo</th>
                <th className="px-2 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((p) => {
                const low = p.stock_min > 0 && p.current_stock <= p.stock_min;
                return (
                  <tr key={p.id} className="border-t border-border hover:bg-muted/30" data-testid={`prod-row-${p.id}`}>
                    <td className="px-4 py-3">
                      <button className="font-medium text-left hover:underline" onClick={() => setDetailId(p.id)}>
                        {p.name}
                      </button>
                      {p.sku && <div className="text-[10px] text-muted-foreground font-mono">{p.sku}</div>}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{p.category || "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1 flex-wrap">
                        <Badge variant="outline">{MODE_LABEL[p.mode]}</Badge>
                        {(() => {
                          // Diagnóstico de visibilidade na loja pública:
                          // • is_public=True + stock>0 + mode!=internal → 🟢 Na loja
                          // • is_public=True + stock=0 → ⚠️ Loja (sem estoque)
                          // • is_public=True + mode=internal → ⚠️ Uso interno
                          if (!p.is_public) return null;
                          const stock = p.current_stock ?? 0;
                          if (p.mode === "internal") {
                            return (
                              <Badge variant="outline"
                                title="Modo 'Uso interno' esconde da loja. Troque para 'Venda' ou 'Ambos'."
                                className="text-[9px] bg-amber-500/10 text-amber-700 border-amber-500/30">
                                ⚠️ Uso interno
                              </Badge>
                            );
                          }
                          if (stock <= 0) {
                            return (
                              <Badge variant="outline"
                                title="Sem estoque. Clique em 'Entrada' para adicionar um lote e o produto aparecerá na loja."
                                className="text-[9px] bg-amber-500/10 text-amber-700 border-amber-500/30">
                                ⚠️ Loja (sem estoque)
                              </Badge>
                            );
                          }
                          return (
                            <Badge variant="outline"
                              title="Produto visível na landing pública."
                              className="text-[9px] bg-green-500/10 text-green-700 border-green-500/30">
                              🟢 Na loja
                            </Badge>
                          );
                        })()}
                      </div>
                    </td>
                    <td className={`px-4 py-3 text-right font-mono whitespace-nowrap ${low ? "text-amber-700 font-semibold" : ""}`}>
                      {p.current_stock ?? 0} {p.unit}
                      {low && <AlertTriangle className="w-3 h-3 inline ml-1" />}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-muted-foreground whitespace-nowrap">{p.stock_min} {p.unit}</td>
                    <td className="px-2 py-3 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => setBatchTarget(p)} data-testid={`prod-add-batch-${p.id}`}>
                        <Plus className="w-3.5 h-3.5 mr-1" /> Entrada
                      </Button>
                      {p.mode !== "internal" && (p.current_stock ?? 0) > 0 && (
                        <Button size="sm" variant="ghost" onClick={() => setSaleTarget(p)} data-testid={`prod-sell-${p.id}`}
                          className="text-tenant hover:text-tenant hover:bg-tenant/10">
                          <ShoppingCart className="w-3.5 h-3.5 mr-1" /> Vender
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => setEditTarget(p)} data-testid={`prod-edit-${p.id}`} title="Editar">
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(p)} data-testid={`prod-delete-${p.id}`} title="Excluir"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          <div className="px-4 py-3 border-t border-border">
            <PaginationBar
              page={safePage} totalPages={totalPages} total={items.length}
              onChange={setPage} label="produto" testid="products-pagination"
            />
          </div>
        </Card>
      )}

      <NewProductDialog open={openNew} onClose={() => setOpenNew(false)} onSaved={load} />
      <EditProductDialog product={editTarget} onClose={() => setEditTarget(null)} onSaved={load} />
      <ConfirmDeleteProductDialog product={deleteTarget} onClose={() => setDeleteTarget(null)} onDeleted={load} />
      <BatchEntryDialog product={batchTarget} onClose={() => setBatchTarget(null)} onSaved={load} />
      <ProductDetailDialog productId={detailId} onClose={() => setDetailId(null)} onChanged={load} />
      <SaleDialog open={!!saleTarget} onClose={() => setSaleTarget(null)} onSaved={load} products={items} preselectedProduct={saleTarget && saleTarget.id ? saleTarget : null} />
    </div>
  );
}

function NewProductDialog({ open, onClose, onSaved }) {
  const empty = { name: "", sku: "", category: "", mode: "both", unit: "un",
                  price_sale: 0, stock_min: 0, stock_ideal: 0, is_public: false, image_url: "", description: "",
                  // primeiro lote (opcional)
                  initial_quantity: 0, initial_expires_at: "", initial_cost: 0 };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(empty); /* eslint-disable-next-line */ }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: prod } = await api.post("/tenant/inventory/products", {
        name: f.name, sku: f.sku, category: f.category, mode: f.mode, unit: f.unit,
        price_sale: Number(f.price_sale) || null,
        stock_min: Number(f.stock_min) || 0,
        stock_ideal: Number(f.stock_ideal) || 0,
        is_public: !!f.is_public,
        image_url: f.image_url || null,
        description: f.description || null,
      });
      const initQty = Number(f.initial_quantity) || 0;
      if (initQty > 0) {
        await api.post("/tenant/inventory/batches", {
          product_id: prod.id,
          quantity: initQty,
          cost_unit: Number(f.initial_cost) || 0,
          expires_at: f.initial_expires_at ? `${f.initial_expires_at}T00:00:00` : null,
        });
        toast.success(`Produto criado com ${initQty} ${f.unit} em estoque!`);
      } else {
        toast.success("Produto criado!");
      }
      onSaved?.();
      onClose();
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-product-dialog">
        <DialogHeader>
          <DialogTitle>Novo produto</DialogTitle>
          <DialogDescription>Ex.: shampoo, ração, alicate, medicamento, insumo de limpeza.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label>Nome *</Label>
            <Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
              data-testid="new-product-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>SKU / Código</Label>
              <Input value={f.sku} onChange={(e) => setF({ ...f, sku: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Categoria</Label>
              <Input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}
                placeholder="Ex.: banho, ração, cosmético" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Modo</Label>
              <Select value={f.mode} onValueChange={(v) => setF({ ...f, mode: v })}>
                <SelectTrigger data-testid="new-product-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">Ambos (venda + interno)</SelectItem>
                  <SelectItem value="sale">Só venda</SelectItem>
                  <SelectItem value="internal">Só uso interno</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Unidade</Label>
              <Input value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })}
                placeholder="un, kg, ml, cx" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Preço venda (R$)</Label>
              <Input type="number" step="0.01" value={f.price_sale} onChange={(e) => setF({ ...f, price_sale: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Estoque mínimo</Label>
              <Input type="number" min="0" value={f.stock_min} onChange={(e) => setF({ ...f, stock_min: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Estoque ideal</Label>
              <Input type="number" min="0" value={f.stock_ideal} onChange={(e) => setF({ ...f, stock_ideal: e.target.value })} />
            </div>
          </div>

          {/* Bloco loja pública — opcional */}
          <div className="rounded-lg border border-dashed border-border p-3 space-y-3 bg-green-500/5">
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={f.is_public}
                onChange={(e) => setF({ ...f, is_public: e.target.checked })}
                data-testid="new-product-is-public" className="w-4 h-4" />
              <ShoppingCart className="w-3.5 h-3.5" />
              <span className="font-medium">Disponível na loja online (landing pública)</span>
            </label>
            {f.is_public && (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">URL da imagem</Label>
                  <Input type="url" placeholder="https://…" data-testid="new-product-image-url"
                    value={f.image_url} onChange={(e) => setF({ ...f, image_url: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Descrição para o cliente</Label>
                  <textarea rows={2} className="w-full text-sm rounded-md border border-border bg-card px-3 py-2"
                    placeholder="Ex.: Perfume amadeirado, notas de baunilha e sândalo, 100ml."
                    data-testid="new-product-description"
                    value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
                </div>
                <p className="text-[11px] text-muted-foreground">
                  O cliente vê o produto na landing pública e pode reservar. A reserva baixa
                  automaticamente o estoque (FIFO) e é registrada como &quot;fiado&quot; até você confirmar o pagamento.
                </p>
                <p className="text-[11px] text-amber-700 bg-amber-500/10 border border-amber-500/30 rounded-md px-2 py-1.5">
                  <b>Importante:</b> o produto só aparece na loja se tiver estoque. Após criar,
                  clique em <b>Entrada</b> na tabela para adicionar um lote.
                </p>
              </div>
            )}
          </div>

          {/* Bloco de estoque inicial + validade — opcional */}
          <div className="rounded-lg border border-dashed border-border p-3 space-y-3 bg-muted/20">
            <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Package className="w-3 h-3" /> Estoque inicial (opcional)
            </div>
            <p className="text-[11px] text-muted-foreground">
              Se preencher a quantidade, o sistema já cria o primeiro lote junto com o produto.
              Se deixar zerado, você cadastra a entrada depois no botão &quot;Entrada&quot;.
            </p>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Quantidade inicial</Label>
                <Input type="number" min="0" step="0.01" value={f.initial_quantity}
                  onChange={(e) => setF({ ...f, initial_quantity: e.target.value })}
                  data-testid="new-product-initial-qty" />
              </div>
              <div className="space-y-1.5">
                <Label>Validade</Label>
                <Input type="date" value={f.initial_expires_at}
                  onChange={(e) => setF({ ...f, initial_expires_at: e.target.value })}
                  data-testid="new-product-initial-expires" />
              </div>
              <div className="space-y-1.5">
                <Label>Custo unit. (R$)</Label>
                <Input type="number" step="0.01" value={f.initial_cost}
                  onChange={(e) => setF({ ...f, initial_cost: e.target.value })} />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={busy} data-testid="new-product-save"
              style={{ background: "hsl(var(--tenant-primary))", color: "hsl(var(--tenant-primary-foreground))" }}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Criar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function EditProductDialog({ product, onClose, onSaved }) {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (product) {
      setF({
        name: product.name || "",
        sku: product.sku || "",
        category: product.category || "",
        mode: product.mode || "both",
        unit: product.unit || "un",
        price_sale: product.price_sale ?? 0,
        stock_min: product.stock_min ?? 0,
        stock_ideal: product.stock_ideal ?? 0,
        is_public: !!product.is_public,
        image_url: product.image_url || "",
        description: product.description || "",
      });
    }
  }, [product]);

  if (!product || !f) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/tenant/inventory/products/${product.id}`, {
        name: f.name,
        sku: f.sku || null,
        category: f.category || null,
        mode: f.mode,
        unit: f.unit,
        price_sale: Number(f.price_sale) || null,
        stock_min: Number(f.stock_min) || 0,
        stock_ideal: Number(f.stock_ideal) || 0,
        is_public: !!f.is_public,
        image_url: f.image_url || null,
        description: f.description || null,
      });
      toast.success("Produto atualizado");
      onSaved?.();
      onClose?.();
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="edit-product-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Editar produto</DialogTitle>
          <DialogDescription>Ajuste os dados de <b>{product.name}</b>.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5 col-span-2">
              <Label>Nome</Label>
              <Input required data-testid="edit-product-name"
                value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>SKU</Label>
              <Input value={f.sku} onChange={(e) => setF({ ...f, sku: e.target.value })}
                data-testid="edit-product-sku" />
            </div>
            <div className="space-y-1.5">
              <Label>Categoria</Label>
              <Input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}
                data-testid="edit-product-category" />
            </div>
            <div className="space-y-1.5">
              <Label>Modo</Label>
              <Select value={f.mode} onValueChange={(v) => setF({ ...f, mode: v })}>
                <SelectTrigger data-testid="edit-product-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sale">Venda</SelectItem>
                  <SelectItem value="internal">Uso interno</SelectItem>
                  <SelectItem value="both">Ambos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Unidade</Label>
              <Input value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })}
                data-testid="edit-product-unit" />
            </div>
            <div className="space-y-1.5">
              <Label>Preço de venda</Label>
              <Input type="number" step="0.01" value={f.price_sale}
                onChange={(e) => setF({ ...f, price_sale: e.target.value })}
                data-testid="edit-product-price" />
            </div>
            <div className="space-y-1.5">
              <Label>Estoque mínimo</Label>
              <Input type="number" step="0.01" value={f.stock_min}
                onChange={(e) => setF({ ...f, stock_min: e.target.value })}
                data-testid="edit-product-stock-min" />
            </div>
            <div className="space-y-1.5">
              <Label>Estoque ideal</Label>
              <Input type="number" step="0.01" value={f.stock_ideal}
                onChange={(e) => setF({ ...f, stock_ideal: e.target.value })}
                data-testid="edit-product-stock-ideal" />
            </div>
          </div>

          <div className="rounded-lg border border-dashed border-border p-3 space-y-3 bg-green-500/5">
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={f.is_public}
                onChange={(e) => setF({ ...f, is_public: e.target.checked })}
                data-testid="edit-product-is-public" className="w-4 h-4" />
              <ShoppingCart className="w-3.5 h-3.5" />
              <span className="font-medium">Disponível na loja online</span>
            </label>
            {f.is_public && (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">URL da imagem</Label>
                  <Input type="url" value={f.image_url}
                    onChange={(e) => setF({ ...f, image_url: e.target.value })}
                    data-testid="edit-product-image-url" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Descrição para o cliente</Label>
                  <textarea rows={2} className="w-full text-sm rounded-md border border-border bg-card px-3 py-2"
                    data-testid="edit-product-description"
                    value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={busy} data-testid="edit-product-submit"
              style={{ background: "hsl(var(--tenant-primary))", color: "hsl(var(--tenant-primary-foreground))" }}>
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />} Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function ConfirmDeleteProductDialog({ product, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  if (!product) return null;
  const doDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/tenant/inventory/products/${product.id}`);
      toast.success(`Produto "${product.name}" excluído`);
      onDeleted?.();
      onClose?.();
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="delete-product-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Excluir produto</DialogTitle>
          <DialogDescription>
            Tem certeza que deseja excluir <b>{product.name}</b>? O produto ficará inativo,
            mas o histórico de vendas e lotes é preservado.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button variant="destructive" onClick={doDelete} disabled={busy} data-testid="delete-product-confirm">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />} Excluir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function BatchEntryDialog({ product, onClose, onSaved }) {
  const empty = { batch_code: "", quantity: 1, cost_unit: 0, expires_at: "", location: "", supplier_id: "" };
  const [f, setF] = useState(empty);
  const [suppliers, setSuppliers] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (product) {
      setF(empty);
      api.get("/tenant/inventory/suppliers").then(({ data }) => setSuppliers(data.items || [])).catch(() => {});
    }
    // eslint-disable-next-line
  }, [product]);

  const submit = async (e) => {
    e.preventDefault();
    if (!product) return;
    setBusy(true);
    try {
      await api.post("/tenant/inventory/batches", {
        product_id: product.id,
        batch_code: f.batch_code || null,
        quantity: Number(f.quantity),
        cost_unit: Number(f.cost_unit) || 0,
        expires_at: f.expires_at ? `${f.expires_at}T00:00:00` : null,
        location: f.location || null,
        supplier_id: f.supplier_id || null,
      });
      toast.success("Entrada registrada!");
      onSaved?.(); onClose();
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!product} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="batch-entry-dialog">
        <DialogHeader>
          <DialogTitle>Entrada de estoque</DialogTitle>
          <DialogDescription>{product?.name}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Quantidade *</Label>
              <Input required type="number" min="0.01" step="0.01" value={f.quantity}
                onChange={(e) => setF({ ...f, quantity: e.target.value })}
                data-testid="batch-quantity" />
            </div>
            <div className="space-y-1.5">
              <Label>Custo unitário</Label>
              <Input type="number" step="0.01" value={f.cost_unit}
                onChange={(e) => setF({ ...f, cost_unit: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Código do lote</Label>
              <Input value={f.batch_code} onChange={(e) => setF({ ...f, batch_code: e.target.value })}
                placeholder="Ex.: NF-1234" />
            </div>
            <div className="space-y-1.5">
              <Label>Vencimento</Label>
              <Input type="date" value={f.expires_at}
                onChange={(e) => setF({ ...f, expires_at: e.target.value })}
                data-testid="batch-expires" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Fornecedor</Label>
            <Select value={f.supplier_id} onValueChange={(v) => setF({ ...f, supplier_id: v })}>
              <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
              <SelectContent>
                {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Localização</Label>
            <Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })}
              placeholder="Ex.: Prateleira A2" />
          </div>
          <DialogFooter>
            <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={busy} data-testid="batch-save"
              style={{ background: "hsl(var(--tenant-primary))", color: "hsl(var(--tenant-primary-foreground))" }}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Registrar entrada"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ProductDetailDialog({ productId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [movOpen, setMovOpen] = useState(false);
  useEffect(() => {
    if (!productId) { setData(null); return; }
    api.get(`/tenant/inventory/products/${productId}`).then(({ data }) => setData(data));
  }, [productId]);

  if (!productId) return null;
  return (
    <Dialog open={!!productId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="product-detail-dialog">
        <DialogHeader>
          <DialogTitle>{data?.product?.name || "Carregando..."}</DialogTitle>
        </DialogHeader>
        {!data ? <Loader2 className="w-4 h-4 animate-spin" /> : (
          <div className="space-y-5">
            <div className="grid grid-cols-4 gap-3 text-sm">
              <MetricPill label="Em estoque" value={`${data.metrics.current_stock} ${data.product.unit}`} />
              <MetricPill label="Consumo 30d" value={`${data.metrics.consumed_last_30d} ${data.product.unit}`} />
              <MetricPill label="Média/dia" value={`${data.metrics.avg_daily_consumption} ${data.product.unit}`} />
              <MetricPill label="Ruptura em" value={data.metrics.days_to_rupture ? `${data.metrics.days_to_rupture}d` : "—"} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold">Lotes</div>
                <Button size="sm" variant="outline" onClick={() => setMovOpen(true)}>
                  <ArrowDownCircle className="w-3.5 h-3.5 mr-1" /> Movimentação manual
                </Button>
              </div>
              {data.batches.length === 0 ? (
                <div className="text-sm text-muted-foreground italic">Sem lotes.</div>
              ) : (
                <div className="border border-border rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40 text-muted-foreground">
                      <tr><th className="text-left px-3 py-2">Código</th><th className="text-left px-3 py-2">Fornecedor</th><th className="text-right px-3 py-2">Estoque</th><th className="text-right px-3 py-2">Custo un.</th><th className="text-left px-3 py-2">Vencimento</th><th className="text-left px-3 py-2">Status</th></tr>
                    </thead>
                    <tbody>
                      {data.batches.map((b) => {
                        const d = daysUntil(b.expires_at);
                        const color = b.status === "expired" ? "text-red-700" :
                          (d != null && d <= 30) ? "text-amber-700" : "";
                        return (
                          <tr key={b.id} className="border-t border-border">
                            <td className="px-3 py-2 font-mono">{b.batch_code || "—"}</td>
                            <td className="px-3 py-2 text-muted-foreground">{b.supplier_name || "—"}</td>
                            <td className="px-3 py-2 text-right font-mono">{b.quantity_current}/{b.quantity_initial}</td>
                            <td className="px-3 py-2 text-right font-mono">{fmtBRL(b.cost_unit)}</td>
                            <td className={`px-3 py-2 ${color}`}>{fmtDate(b.expires_at)} {d != null && d < 60 && <span className="text-[10px]">({d}d)</span>}</td>
                            <td className="px-3 py-2"><Badge variant="outline">{b.status}</Badge></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div>
              <div className="text-sm font-semibold mb-2">Últimas movimentações</div>
              {data.movements.length === 0 ? (
                <div className="text-sm text-muted-foreground italic">Sem movimentações.</div>
              ) : (
                <ul className="space-y-1 text-xs max-h-40 overflow-y-auto">
                  {data.movements.map((m) => (
                    <li key={m.id} className="flex items-center justify-between border-b border-border/60 py-1.5">
                      <span>
                        <span className={m.quantity > 0 ? "text-emerald-700 font-mono" : "text-red-700 font-mono"}>
                          {m.quantity > 0 ? `+${m.quantity}` : m.quantity}
                        </span>
                        {" "}<Badge variant="outline">{REASON_LABEL[m.reason] || m.reason}</Badge>
                        {m.batch_code && <span className="ml-1 font-mono text-muted-foreground">[{m.batch_code}]</span>}
                        {m.notes && <span className="ml-2 text-muted-foreground">— {m.notes}</span>}
                      </span>
                      <span className="text-muted-foreground">{new Date(m.created_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
        <ManualMovementDialog
          open={movOpen} onClose={() => setMovOpen(false)}
          product={data?.product} batches={data?.batches || []}
          onSaved={async () => {
            setMovOpen(false);
            const { data: d } = await api.get(`/tenant/inventory/products/${productId}`);
            setData(d); onChanged?.();
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

function MetricPill({ label, value }) {
  return (
    <div className="p-3 rounded-lg bg-muted/30 border border-border">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-display font-semibold text-lg mt-1">{value}</div>
    </div>
  );
}

function ManualMovementDialog({ open, onClose, product, batches, onSaved }) {
  const empty = { quantity: 1, reason: "loss", batch_id: "", notes: "", forced: false, forced_reason: "" };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(empty); /* eslint-disable-next-line */ }, [open]);
  if (!product) return null;

  const selectedBatch = batches.find((b) => b.id === f.batch_id);
  const isExpiredBatch = selectedBatch?.status === "expired";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/tenant/inventory/movements", {
        product_id: product.id,
        quantity: -Math.abs(Number(f.quantity)),
        reason: f.reason,
        batch_id: f.batch_id || null,
        notes: f.notes || null,
        forced: isExpiredBatch ? true : f.forced,
        forced_reason: isExpiredBatch ? (f.forced_reason || "Baixa de lote vencido") : (f.forced_reason || null),
      });
      toast.success("Movimentação registrada!");
      onSaved?.();
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="manual-movement-dialog">
        <DialogHeader>
          <DialogTitle>Movimentação manual</DialogTitle>
          <DialogDescription>{product.name}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Quantidade *</Label>
              <Input required type="number" min="0.01" step="0.01" value={f.quantity}
                onChange={(e) => setF({ ...f, quantity: e.target.value })} />
              <p className="text-[10px] text-muted-foreground">Baixa (será registrada como saída).</p>
            </div>
            <div className="space-y-1.5">
              <Label>Motivo</Label>
              <Select value={f.reason} onValueChange={(v) => setF({ ...f, reason: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="loss">Perda</SelectItem>
                  <SelectItem value="breakage">Quebra</SelectItem>
                  <SelectItem value="expiry">Vencimento</SelectItem>
                  <SelectItem value="discard">Descarte</SelectItem>
                  <SelectItem value="internal_use">Uso interno</SelectItem>
                  <SelectItem value="return_supplier">Devolução ao fornecedor</SelectItem>
                  <SelectItem value="adjustment">Ajuste</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Lote (opcional)</Label>
            <Select value={f.batch_id} onValueChange={(v) => setF({ ...f, batch_id: v === "__auto__" ? "" : v })}>
              <SelectTrigger><SelectValue placeholder="FIFO automático (menor validade)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__auto__">FIFO automático</SelectItem>
                {batches.filter((b) => b.quantity_current > 0).map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.batch_code || b.id.slice(0, 8)} · {b.quantity_current} un · venc {fmtDate(b.expires_at)} · {b.status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {isExpiredBatch && (
            <div className="p-3 rounded-md bg-amber-500/10 border border-amber-500/30 text-xs space-y-2">
              <div className="flex items-start gap-2 text-amber-900">
                <Info className="w-4 h-4 shrink-0" />
                <div>
                  <div className="font-semibold">Lote vencido — baixa forçada</div>
                  <div>Registre o motivo pra ficar rastreável na auditoria.</div>
                </div>
              </div>
              <Input value={f.forced_reason}
                onChange={(e) => setF({ ...f, forced_reason: e.target.value })}
                placeholder="Ex.: descarte controlado" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Notas</Label>
            <Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
          </div>
          <DialogFooter>
            <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={busy}
              style={{ background: "hsl(var(--tenant-primary))", color: "hsl(var(--tenant-primary-foreground))" }}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Registrar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const PAYMENT_METHODS = [
  { value: "dinheiro", label: "Dinheiro" },
  { value: "pix", label: "Pix" },
  { value: "cartao_debito", label: "Cartão de débito" },
  { value: "cartao_credito", label: "Cartão de crédito" },
  { value: "fiado", label: "Fiado (a receber)" },
];

function SaleDialog({ open, onClose, onSaved, products, preselectedProduct }) {
  const [items, setItems] = useState([{ product_id: "", quantity: 1, unit_price: 0 }]);
  const [method, setMethod] = useState("pix");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      if (preselectedProduct?.id) {
        setItems([{
          product_id: preselectedProduct.id,
          quantity: 1,
          unit_price: preselectedProduct.price_sale || 0,
        }]);
      } else {
        setItems([{ product_id: "", quantity: 1, unit_price: 0 }]);
      }
      setMethod("pix");
    }
  }, [open, preselectedProduct?.id]);
  const sellable = useMemo(() => products.filter((p) => p.mode !== "internal"), [products]);

  const update = (i, field, val) => {
    setItems((arr) => arr.map((it, idx) => idx === i ? { ...it, [field]: val } : it));
    if (field === "product_id") {
      const p = sellable.find((x) => x.id === val);
      if (p?.price_sale) setItems((arr) => arr.map((it, idx) => idx === i ? { ...it, unit_price: p.price_sale } : it));
    }
  };
  const total = items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/tenant/inventory/sales", {
        items: items.filter((i) => i.product_id && i.quantity > 0).map((i) => ({
          product_id: i.product_id, quantity: Number(i.quantity),
          unit_price: Number(i.unit_price),
        })),
        payment_method: method,
      });
      toast.success(`Venda registrada! ${fmtBRL(total)}`);
      onSaved?.(); onClose();
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="sale-dialog">
        <DialogHeader>
          <DialogTitle>Venda avulsa</DialogTitle>
          <DialogDescription>Registra a venda e deduz automaticamente do estoque (FIFO).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-[1fr_80px_100px_auto] gap-2 items-end">
              <div>
                <Label className="text-xs">Produto</Label>
                <Select value={it.product_id} onValueChange={(v) => update(i, "product_id", v)}>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {sellable.map((p) => (
                      <SelectItem key={p.id} value={p.id} disabled={p.current_stock <= 0}>
                        {p.name} ({p.current_stock} {p.unit})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Qtd</Label>
                <Input type="number" min="0.01" step="0.01" value={it.quantity} onChange={(e) => update(i, "quantity", e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Preço un.</Label>
                <Input type="number" step="0.01" value={it.unit_price} onChange={(e) => update(i, "unit_price", e.target.value)} />
              </div>
              <Button type="button" size="icon" variant="ghost" onClick={() => setItems((a) => a.filter((_, x) => x !== i))} disabled={items.length === 1}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={() => setItems((a) => [...a, { product_id: "", quantity: 1, unit_price: 0 }])}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar item
          </Button>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Pagamento</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="sale-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m.value} value={m.value} data-testid={`sale-method-${m.value}`}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end justify-end">
              <div className="text-right">
                <div className="text-[10px] uppercase text-muted-foreground">Total</div>
                <div className="font-display font-bold text-2xl">{fmtBRL(total)}</div>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={busy || total <= 0}
            style={{ background: "hsl(var(--tenant-primary))", color: "hsl(var(--tenant-primary-foreground))" }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Registrar venda"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Suppliers
// -----------------------------------------------------------------------------
function SuppliersList() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", phone: "", email: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const load = async () => {
    const { data } = await api.get("/tenant/inventory/suppliers");
    setItems(data.items || []);
  };
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/tenant/inventory/suppliers", f); toast.success("Fornecedor criado"); setOpen(false); setF({ name: "", phone: "", email: "", notes: "" }); load(); }
    catch (er) { toast.error(formatApiError(er)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="sup-new" style={{ background: "hsl(var(--tenant-primary))", color: "hsl(var(--tenant-primary-foreground))" }}><Plus className="w-4 h-4 mr-1" /> Novo fornecedor</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Novo fornecedor</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Nome *</Label><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
              <div><Label>Telefone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
              <div><Label>Email</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
              <div><Label>Notas</Label><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
              <DialogFooter>
                <Button variant="ghost" type="button" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button type="submit" disabled={busy}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Criar"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      {items.length === 0 ? (
        <Card className="p-10 text-center text-sm text-muted-foreground">Sem fornecedores cadastrados.</Card>
      ) : (
        <Card className="rounded-tenant border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
              <tr><th className="text-left px-4 py-2">Nome</th><th className="text-left px-4 py-2">Telefone</th><th className="text-left px-4 py-2">Email</th></tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} className="border-t border-border"><td className="px-4 py-2 font-medium">{s.name}</td><td className="px-4 py-2 text-muted-foreground">{s.phone || "—"}</td><td className="px-4 py-2 text-muted-foreground">{s.email || "—"}</td></tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
